import { useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { TbTrash, TbCheck, TbX, TbPencil } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import Toast, { type ToastState } from "src/components/Toast/Toast";
import { apiRequest, ApiError } from "src/services/apiClient";
import { useApi } from "src/hooks/useApi";
import { getTagStyle } from "src/helpers/randomColor";
import type { Tag } from "src/types";
import "./manageTagsPopup.scss";

export function ManageTagsPopup({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const { data: tags, loading, refetch } = useApi<Tag[]>("/tags");
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [savingId, setSavingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState<ToastState | null>(null);

    const handleCreate = async (e: FormEvent) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name) return;
        setCreating(true);
        try {
            await apiRequest("/tags", { method: "POST", body: JSON.stringify({ name }) }, { "Content-Type": "application/json" });
            setNewName("");
            await refetch();
        } catch (err) {
            setToast({ message: err instanceof ApiError ? err.message : t("tags.createFailed"), variant: "error" });
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (tag: Tag) => { setEditingId(tag.id); setEditName(tag.name); };
    const cancelEdit = () => { setEditingId(null); setEditName(""); };

    const handleRename = async (id: string) => {
        const name = editName.trim();
        if (!name) return;
        setSavingId(id);
        try {
            await apiRequest(`/tags/${id}`, { method: "PUT", body: JSON.stringify({ name }) }, { "Content-Type": "application/json" });
            setEditingId(null);
            await refetch();
        } catch (err) {
            setToast({ message: err instanceof ApiError ? err.message : t("tags.renameFailed"), variant: "error" });
        } finally {
            setSavingId(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await apiRequest(`/tags/${deleteTarget.id}`, { method: "DELETE" });
            setDeleteTarget(null);
            await refetch();
        } catch (err) {
            setToast({ message: err instanceof ApiError ? err.message : t("tags.deleteFailed"), variant: "error" });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
            <Popup
                title={t("tags.manageTags")}
                className="manage-tags-popup"
                onClose={onClose}
                actions={[{ label: t("popup.done"), variant: "primary", onClick: onClose }]}
                message={
                    <div className="manage-tags">
                        <form className="manage-tags__add" onSubmit={handleCreate}>
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder={t("tags.newTagPlaceholder")}
                                maxLength={40}
                            />
                            <button type="submit" className="popup__btn popup__btn--primary" disabled={creating || !newName.trim()}>
                                {t("tags.addTag")}
                            </button>
                        </form>

                        {loading ? (
                            <p className="manage-tags__empty">{t("common.loading")}</p>
                        ) : !tags?.length ? (
                            <p className="manage-tags__empty">{t("tags.noTags")}</p>
                        ) : (
                            <ul className="manage-tags__list">
                                {tags.map((tag) => (
                                    <li key={tag.id} className="manage-tags__row">
                                        <span
                                            className="manage-tags__chip"
                                            style={getTagStyle(tag.name) as CSSProperties}
                                        >
                                            {tag.name}
                                        </span>
                                        {editingId === tag.id ? (
                                            <div className="manage-tags__edit">
                                                <input
                                                    type="text"
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    maxLength={40}
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    className="manage-tags__icon-btn"
                                                    onClick={() => handleRename(tag.id)}
                                                    disabled={savingId === tag.id || !editName.trim()}
                                                >
                                                    <TbCheck />
                                                </button>
                                                <button type="button" className="manage-tags__icon-btn" onClick={cancelEdit}>
                                                    <TbX />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="manage-tags__actions">
                                                <button
                                                    type="button"
                                                    className="manage-tags__icon-btn"
                                                    onClick={() => startEdit(tag)}
                                                    title={t("tags.rename")}
                                                >
                                                    <TbPencil />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="manage-tags__icon-btn manage-tags__icon-btn--danger"
                                                    onClick={() => setDeleteTarget(tag)}
                                                    title={t("tags.delete")}
                                                >
                                                    <TbTrash />
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                }
            />

            {deleteTarget && (
                <Popup
                    title={t("tags.delete")}
                    message={t("tags.deleteConfirm", { name: deleteTarget.name })}
                    onClose={() => setDeleteTarget(null)}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setDeleteTarget(null) },
                        { label: t("popup.ok"), variant: "danger", disabled: deleting, onClick: handleDelete },
                    ]}
                />
            )}
        </>
    );
}

export default ManageTagsPopup;
